import type { AtAGlanceEntityContext, AtAGlanceLinkedEntity } from '../atAGlanceTypes.ts';

const mergeEntityContext = (
	primary: AtAGlanceEntityContext | undefined,
	secondary: AtAGlanceEntityContext | undefined,
): AtAGlanceEntityContext | undefined => {
	if (!primary) {
		return secondary;
	}
	if (!secondary) {
		return primary;
	}

	const summary = primary.summary ?? secondary.summary;
	const status = primary.status ?? secondary.status;
	const updatedAt = primary.updated_at ?? secondary.updated_at;
	return {
		...(summary ? { summary } : {}),
		...(status ? { status } : {}),
		...(updatedAt ? { updated_at: updatedAt } : {}),
	};
};

/**
 * Deduplicates entities by ARI and keeps one relationship per collection. An entity reached
 * through several collections carries all of them: each names a different thing the page and the
 * entity have to do with each other, and only the `links_to` relationship can carry a
 * `source_excerpt`. The count needs no limit of its own — `AtAGlanceCollection` has four members,
 * so four relationships is the ceiling.
 */
export const mergeAtAGlanceEvidence = (
	entities: readonly AtAGlanceLinkedEntity[],
): AtAGlanceLinkedEntity[] => {
	const byAri = new Map<string, AtAGlanceLinkedEntity>();

	for (const entity of entities) {
		const existing = byAri.get(entity.ari);
		const nextRelationship = entity.relationships[0];
		if (!existing) {
			byAri.set(entity.ari, entity);
			continue;
		}
		if (!nextRelationship) {
			continue;
		}

		const existingRelationshipIndex = existing.relationships.findIndex(
			(relationship) => relationship.collection === nextRelationship.collection,
		);
		const relationships = [...existing.relationships];
		if (existingRelationshipIndex === -1) {
			relationships.push(nextRelationship);
		} else {
			const existingRelationship = existing.relationships[existingRelationshipIndex];
			if (!existingRelationship) {
				continue;
			}
			if (!existingRelationship.source_excerpt && nextRelationship.source_excerpt) {
				relationships[existingRelationshipIndex] = {
					...existingRelationship,
					source_excerpt: nextRelationship.source_excerpt,
				};
			}
		}

		const entityContext = mergeEntityContext(existing.entity_context, entity.entity_context);
		byAri.set(entity.ari, {
			...existing,
			product: existing.product ?? entity.product,
			resource_type: existing.resource_type ?? entity.resource_type,
			relationships,
			...(entityContext ? { entity_context: entityContext } : {}),
		});
	}

	return [...byAri.values()];
};
